import { TOOL_COUNT } from '../content/toolCount';
import { BENCHMARK_LABEL, BENCHMARK_AWARD } from '../content/benchmarkClaim';
/**
 * Structured Data (JSON-LD) generators for SEO
 * These help Google understand your content and display rich snippets
 */

import { SEO_DEFAULTS } from './seoConfig';

/**
 * Generate Organization schema
 * Used on homepage and about pages
 */
export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SEO_DEFAULTS.url}/#organization`,
  name: 'CellCog',
  url: SEO_DEFAULTS.url,
  logo: `${SEO_DEFAULTS.url}/logo512.png`,
  description: `CellCog builds AI Employees you can hire for any role, a Super Agent with deep reasoning across every modality (research, data, code, documents, images, video), and an Agent-to-Agent platform that opens the whole system to other AI agents. ${BENCHMARK_LABEL}.`,
  foundingDate: '2025',
  // sameAs = official profiles only, each verified live before adding (CEL-2097,
  // 2026-09-02). Entity disambiguation for answer engines; never add an unverified URL.
  sameAs: [
    'https://www.linkedin.com/company/cellcog-ai',
    'https://twitter.com/cellcogai',
    'https://www.producthunt.com/products/cellcog',
    'https://github.com/CellCog',
    'https://pypi.org/project/cellcog/',
    'https://www.youtube.com/@CellCog',
    'https://clawhub.ai/cellcog',
    // G2 profile, verified live 2026-09-27 (claimed, 4.9/5, 5 reviews). Entity link ONLY:
    // never copy G2's rating into our own aggregateRating (Google: "Don't aggregate
    // reviews or ratings from other websites").
    'https://www.g2.com/products/cellcog/reviews',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'support@cellcog.ai',
  },
  // Official business address (Nitish, 2026-08-06, CEL-972 — GeoZ location feedback)
  address: {
    '@type': 'PostalAddress',
    streetAddress: '100 Dudley St',
    addressLocality: 'Jersey City',
    addressRegion: 'NJ',
    postalCode: '07302',
    addressCountry: 'US',
  },
});

/**
 * Generate SoftwareApplication schema
 * Helps Google understand CellCog as a software product
 */
export const generateSoftwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: SEO_DEFAULTS.description,
  url: SEO_DEFAULTS.url,
  offers: {
    '@type': 'Offer',
    price: '8',
    priceCurrency: 'USD',
    description:
      'Plans from $8/month. AI employee usage is pay-per-work; the cost depends purely on how much work you assign. Start free, no credit card needed.',
  },
  // NOTE: aggregateRating intentionally omitted — we do not have verified
  // first-party review data. Adding a fabricated rating violates data integrity
  // and risks a Google structured-data penalty. Re-add only with real reviews.
});

/**
 * Generate Article schema for blog posts
 * @param {Object} post - Post data
 */
export const generateArticleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: post.title,
  description: post.content?.substring(0, 160) || post.title,
  image: post.media_files?.[0]?.url || SEO_DEFAULTS.image,
  datePublished: post.created_at,
  dateModified: post.updated_at || post.created_at,
  author: {
    '@type': 'Organization',
    name: 'CellCog',
    url: SEO_DEFAULTS.url,
  },
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_DEFAULTS.url}/logo512.png`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SEO_DEFAULTS.url}/posts/${post.id}`,
  },
});

/**
 * Generate FAQPage schema from a list of {question, answer} items.
 * One of the highest-leverage GEO provisions — AI answer engines quote FAQ
 * Q&A directly. Use on any page with a real, visible FAQ section (the schema
 * must match the on-page content exactly).
 * @param {Array<{question: string, answer: string}>} items
 */
export const generateFAQSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  // Accepts both FAQ item shapes in use across pages: {question, answer} and {q, a}.
  // (Callers passing {q, a} directly previously emitted Questions with no name/text.)
  mainEntity: (items || []).map((item) => ({
    '@type': 'Question',
    name: item.question || item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer || item.a,
    },
  })),
});

/**
 * Generate WebSite schema with search action
 * Enables sitelinks searchbox in Google
 */
export const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'CellCog',
  url: SEO_DEFAULTS.url,
  description: SEO_DEFAULTS.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SEO_DEFAULTS.url}/posts?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Generate SoftwareApplication schema for the Linear integration page
 * Used on cellcog.ai/linear
 */
export const generateLinearIntegrationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog for Linear',
  applicationCategory: 'ProductivityApplication',
  applicationSubCategory: 'AI Agent',
  operatingSystem: 'Web',
  description:
    "The first agent in Linear that isn't a coder. Assign Linear issues to @CellCog for research, presentations, PDFs, images, video, and interactive dashboards.",
  url: `${SEO_DEFAULTS.url}/linear`,
  offers: {
    '@type': 'Offer',
    price: '8',
    priceCurrency: 'USD',
    description: 'Pay per task with credits from your CellCog account. Plans from $8/month.',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    url: SEO_DEFAULTS.url,
  },
  featureList: [
    'Native Linear AgentSession integration',
    'Two-way context via Linear GraphQL',
    'Multi-user sender identity resolution',
    'Team-based artifact access',
    'Multimodal output: research, presentations, PDFs, images, video, dashboards',
  ],
});

/**
 * Generate SoftwareApplication schema for the OpenClaw integration page.
 * Used on cellcog.ai/openclaw.
 */
export const generateOpenClawIntegrationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog for OpenClaw',
  applicationCategory: 'DeveloperApplication',
  applicationSubCategory: 'AI Agent Execution Layer',
  operatingSystem: 'Web',
  description:
    'The any-to-any execution layer for OpenClaw agents. Delegate research, documents, images, video, audio, and dashboards with one API key.',
  url: `${SEO_DEFAULTS.url}/openclaw`,
  offers: {
    '@type': 'Offer',
    price: '8',
    priceCurrency: 'USD',
    description: 'Pay-per-execution billing from your CellCog account. Plans from $8/month.',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    url: SEO_DEFAULTS.url,
  },
  featureList: [
    'Any-to-any multimodal execution via unified SDK',
    'ClawHub skill install or OpenClaw plugin install',
    'Notify-on-completion daemon pattern for long tasks',
    'Automatic progress updates every 4 minutes',
    'Auto-discovered growing skills catalog',
    'Python SDK with unified response shape',
  ],
});

/**
 * Generate SoftwareApplication schema for the AI Employees page.
 * Used on cellcog.ai/ai_employees.
 */
export const generateAIEmployeesSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog AI Employees',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'AI Employee',
  operatingSystem: 'Web',
  description:
    "An AI employee you can hire for any role, with their own inbox, channels, task board, dashboards, and memory across shifts. Built on CellCog, a super-agent with deep reasoning across research, data, code, and media. They work real shifts and are available on-demand 24/7.",
  award: BENCHMARK_AWARD,
  url: `${SEO_DEFAULTS.url}/ai-employees`,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'Hire an AI employee. Work is billed from your CellCog account.',
  },
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    url: SEO_DEFAULTS.url,
  },
  featureList: [
    'General-purpose AI employee for any role',
    'Deep reasoning across every modality (research, data, code, documents, images, video, dashboards)',
    'Their own email inbox with autonomous action',
    'Live task board with employee-to-employee and human handoffs',
    'Self-built dashboards tracking real KPIs',
    'Persistent memory and shift-based continuity',
  ],
});

/**
 * Generate SoftwareApplication schema for the Super-Agents page.
 * Used on cellcog.ai/super-agents.
 */
export const generateSuperAgentsSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog Super-Agent',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'AI Super-Agent',
  operatingSystem: 'Web',
  description:
    `Chat, coding, co-work, research, and media in one super-agent. World-class deep reasoning across every kind of work: generate video, PDFs, apps, and research from a single prompt, not just code. ${BENCHMARK_LABEL}.`,
  award: BENCHMARK_AWARD,
  url: `${SEO_DEFAULTS.url}/super-agents`,
  offers: {
    '@type': 'Offer',
    price: '8',
    priceCurrency: 'USD',
    description: 'Plans from $8/month.',
  },
  publisher: { '@type': 'Organization', name: 'CellCog', url: SEO_DEFAULTS.url },
  featureList: [
    'Deep-reasoning multi-agent chat',
    'Co-work on your real machine (terminal, files, Chrome)',
    'Every modality: video, images, audio, music, PDFs, decks, apps, dashboards, 3D',
    'One prompt to a finished artifact',
    BENCHMARK_LABEL,
  ],
});

/**
 * Generate SoftwareApplication schema for the Agent-to-Agent page.
 * Used on cellcog.ai/agent-to-agent.
 */
export const generateAgentToAgentSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CellCog Agent-to-Agent',
  applicationCategory: 'DeveloperApplication',
  applicationSubCategory: 'AI Agent Execution Layer',
  operatingSystem: 'Web',
  description:
    'Every software will have two interfaces: one for humans, one for agents. CellCog exposes its entire platform to other agents — OpenClaw, Claude Code, Cursor, Hermes, Linear — so any agent can use every CellCog capability, just like a human.',
  award: BENCHMARK_AWARD,
  keywords: 'API for AI agents, agent-to-agent platform, OpenClaw skills, Hermes agent SDK, Claude Code plugin, Cursor plugin, Linear AI agent, AI agent execution layer',
  url: `${SEO_DEFAULTS.url}/agent-to-agent`,
  offers: {
    '@type': 'Offer',
    price: '8',
    priceCurrency: 'USD',
    description: 'Pay-per-execution billing. Plans from $8/month.',
  },
  publisher: { '@type': 'Organization', name: 'CellCog', url: SEO_DEFAULTS.url },
  featureList: [
    'Full CellCog platform exposed to any agent',
    'Integrations: OpenClaw, Claude Code, Cursor, Hermes, Linear',
    'Every modality via one API key',
    'Growing skills catalog auto-discovered by agents',
    '~30% of CellCog chats are started by other agents',
  ],
});

/**
 * Generate BreadcrumbList schema
 * @param {Array} items - Array of {name, url} objects
 */
/**
 * Generate Article schema for the founder-story page (/story).
 * Author is a Person (the founder) — richer signal for E-E-A-T than an
 * Organization author on a first-person letter.
 */
export const generateStorySchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'The CellCog Story',
  description:
    "How CellCog was built with a CellCog coding agent into the AI super-app that reached #1 on DeepResearch Bench (July 2026, and again in October 2026): the first cell of Cellular Multi-Agents, a self-organizing network of billions of agents.",
  image: `${SEO_DEFAULTS.url}/assets/story/og-story.png`,
  datePublished: '2026-07-10',
  author: {
    '@type': 'Person',
    name: 'Nitish Garg',
    jobTitle: 'Founder',
    image: `${SEO_DEFAULTS.url}/assets/story/founder-avatar.jpg`,
    // sameAs links the author to his public identity graph (E-E-A-T signal;
    // helps Google associate the letter with the real person).
    sameAs: [
      'https://www.linkedin.com/in/gargnitish/',
      'https://x.com/nitishxgarg',
    ],
    worksFor: {
      '@type': 'Organization',
      name: 'CellCog',
      url: SEO_DEFAULTS.url,
    },
  },
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_DEFAULTS.url}/logo512.png`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SEO_DEFAULTS.url}/story`,
  },
});

/**
 * Generate VideoObject schema for the homepage hero walkthrough.
 *
 * SELECTIVE BY DESIGN (Nitish, Jul 25): this is the ONLY video on the site with
 * VideoObject markup. The /super-agents proof-wall videos (trailblazer,
 * last_transmission, tang_sanzang, coffee-ad) deliberately carry NO schema —
 * they are single-prompt art demos, and video-indexing them would put ads and
 * anime shorts into Google Video results under the CellCog brand, muddying the
 * "hire an AI employee" story. One canonical brand video, one clear story.
 *
 * `uploadDate` is required by Google; keep it in sync when the asset is
 * re-cut. Captions are declared so the video can qualify for the
 * "captions available" signal.
 */
export const generateHeroVideoSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'Hire an AI employee on CellCog — the full walkthrough',
  description:
    'The full hire walkthrough: create an account, hire a Chief Marketing Officer, set her tool permissions, then watch day one — she researches the business, ships a live marketing KPI dashboard, works her task board, emails a recap, and writes a handover for the next shift. Pausing her mid-shift is one message.',
  thumbnailUrl: [`${SEO_DEFAULTS.url}/assets/cellcog/hire-journey-poster.jpg?v=7`],
  uploadDate: '2026-08-08T00:00:00-04:00',
  duration: 'PT1M21S',
  contentUrl: `${SEO_DEFAULTS.url}/assets/cellcog/hire-journey-web.mp4`,
  embedUrl: SEO_DEFAULTS.url,
  isFamilyFriendly: true,
  inLanguage: 'en',
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_DEFAULTS.url}/logo512.png`,
    },
  },
  potentialAction: {
    '@type': 'SeekToAction',
    target: `${SEO_DEFAULTS.url}?t={seek_to_second_number}`,
    'startOffset-input': 'required name=seek_to_second_number',
  },
  hasPart: [
    {
      '@type': 'Clip',
      name: 'Hire a role in plain words',
      startOffset: 11,
      endOffset: 21,
      url: `${SEO_DEFAULTS.url}?t=11`,
    },
    {
      '@type': 'Clip',
      name: 'Set permissions across ' + TOOL_COUNT + ' apps',
      startOffset: 21,
      endOffset: 29,
      url: `${SEO_DEFAULTS.url}?t=21`,
    },
    {
      '@type': 'Clip',
      name: 'You are always in control — say stop, she stops',
      startOffset: 38,
      endOffset: 45,
      url: `${SEO_DEFAULTS.url}?t=38`,
    },
    {
      '@type': 'Clip',
      name: 'Day one: report, live dashboard, task board, inbox',
      startOffset: 45,
      endOffset: 62,
      url: `${SEO_DEFAULTS.url}?t=45`,
    },
    {
      '@type': 'Clip',
      name: 'Schedules, wake conditions, and the shift handover',
      startOffset: 62,
      endOffset: 76,
      url: `${SEO_DEFAULTS.url}?t=62`,
    },
  ],
});

/**
 * VideoObject schema for the product tour on /ai-employees (page cut v11.1, 74s).
 * Same contract as generateHeroVideoSchema: uploadDate required by Google —
 * keep it in sync when the asset is re-cut. Clip offsets match the tour's
 * actual scene timings (silent film, on-frame captions, soft music — no
 * narration track).
 */
export const generateProductTourVideoSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'The CellCog product tour: hire an AI employee and see everything they can do',
  description:
    'A quick tour of hiring an AI employee on CellCog: hire with a single line, meet her in chat, grant tool permissions across ' + TOOL_COUNT + ' apps, connect your computer and Chrome, see her own email inbox and wake conditions, her dashboard with KPIs, tasks, and shifts, watch employees coordinate in team channels, read her end-of-shift report, and see the AI organization they form.',
  thumbnailUrl: [`${SEO_DEFAULTS.url}/assets/cellcog/product-tour-page-poster.jpg?v=3`],
  uploadDate: '2026-08-06T00:00:00-04:00',
  duration: 'PT1M14S',
  contentUrl: `${SEO_DEFAULTS.url}/assets/cellcog/product-tour-page.mp4`,
  embedUrl: `${SEO_DEFAULTS.url}/ai-employees`,
  isFamilyFriendly: true,
  inLanguage: 'en',
  publisher: {
    '@type': 'Organization',
    name: 'CellCog',
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_DEFAULTS.url}/logo512.png`,
    },
  },
  potentialAction: {
    '@type': 'SeekToAction',
    target: `${SEO_DEFAULTS.url}/ai-employees?t={seek_to_second_number}`,
    'startOffset-input': 'required name=seek_to_second_number',
  },
  // Clip offsets synced to the v11.1 page cut's real scene timings (the VTT
  // at /assets/cellcog/product-tour-page.en.vtt is the source of truth).
  hasPart: [
    {
      '@type': 'Clip',
      name: 'Hire an AI employee with a single line',
      startOffset: 5,
      endOffset: 10,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=5`,
    },
    {
      '@type': 'Clip',
      name: 'She introduces herself and starts working in chat',
      startOffset: 10,
      endOffset: 20,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=10`,
    },
    {
      '@type': 'Clip',
      name: 'Grant tool permissions across ' + TOOL_COUNT + ' apps',
      startOffset: 20,
      endOffset: 28,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=20`,
    },
    {
      '@type': 'Clip',
      name: 'Her own email inbox and wake conditions',
      startOffset: 34,
      endOffset: 46,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=34`,
    },
    {
      '@type': 'Clip',
      name: 'Her dashboard: KPIs, tasks, and shifts',
      startOffset: 47,
      endOffset: 56,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=47`,
    },
    {
      '@type': 'Clip',
      name: 'Team channels, the shift report, and the AI org chart',
      startOffset: 56,
      endOffset: 74,
      url: `${SEO_DEFAULTS.url}/ai-employees?t=56`,
    },
  ],
});

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

/**
 * Component to inject JSON-LD structured data
 * @param {Object} data - The structured data object
 */
export const JsonLd = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

export default JsonLd;
